import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Product } from '@interfaces/product';

/** Segmento da rota do hub, sob `/products/[code]`. */
export const ONBOARDING_HUB_ROUTE = 'onboarding';

export const buildOnboardingHubHref = (code: Product['code']) =>
  `/products/${code}/${ONBOARDING_HUB_ROUTE}`;

/**
 * Marca que a navegação partiu do hub, para a seta de voltar do layout devolver
 * a pessoa a ele em vez de subir para a home da comunidade.
 *
 * ## Por que NÃO vai no querystring
 *
 * A primeira versão usava `?voltar=onboarding`, e isso quebrava as páginas de
 * destino. **Dezenove lugares neste projeto compõem URL concatenando segmento em
 * `router.asPath`** — `Offers/index.tsx`, `Packs/index.tsx`,
 * `TelegramGroupsConfigs/index.tsx`, o próprio menu do produto e mais. Com um
 * parâmetro na URL, `${asPath}/create` produz
 * `/products/ABC/offers?voltar=onboarding/create`: o pathname continua sendo
 * `/offers`, e o botão "Adicionar" da página de destino deixa de levar a lugar
 * nenhum.
 *
 * Ou seja: o parâmetro não falhava em três arquivos por descuido — ele é
 * incompatível com um padrão que atravessa o projeto. Consertar os dezenove pontos
 * é mudança de outra ordem, e nas páginas que esta entrega se propôs a não tocar.
 *
 * ## Por que `sessionStorage` serve
 *
 * Sobrevive a recarregar a página do destino, que era a exigência real. Não sobrevive
 * a abrir o destino em outra aba — e não precisa: aba nova não é "fazer a tarefa e
 * voltar".
 *
 * ## O que o marcador guarda, e por que não é só a comunidade
 *
 * Guarda a comunidade **e o destino**. Só a comunidade não basta: indo do hub para
 * ofertas e de lá para `/offers/create`, a seta daquela tela também apontaria para o
 * hub — pulando a lista de ofertas, que é o pai legítimo dela. E o marcador seguiria
 * valendo para as outras páginas daquela comunidade até o hub ser revisitado.
 *
 * Com o destino gravado, a volta ao hub vale exatamente na página que o hub abriu.
 * Descendo mais um nível, o comportamento normal reassume sozinho — sem precisar
 * limpar nada em cada navegação.
 *
 * O hub limpa ao montar de qualquer forma, porque aí a viagem terminou.
 */
const CHAVE_VOLTA = 'vibx_voltar_hub';

type Volta = { code: string; destino: string };

export const marcarVoltaParaHub = (code: Product['code'], destino: string) => {
  try {
    sessionStorage.setItem(
      CHAVE_VOLTA,
      JSON.stringify({ code: String(code), destino }),
    );
  } catch {
    // Modo privado ou storage cheio: perde-se a volta, não a navegação.
  }
};

export const lerVoltaParaHub = (): Volta | null => {
  try {
    const cru = sessionStorage.getItem(CHAVE_VOLTA);
    if (!cru) return null;

    const volta = JSON.parse(cru);

    return typeof volta?.code === 'string' && typeof volta?.destino === 'string'
      ? volta
      : null;
  } catch {
    // Inclui JSON inválido de uma versão anterior do marcador.
    return null;
  }
};

export const limparVoltaParaHub = () => {
  try {
    sessionStorage.removeItem(CHAVE_VOLTA);
  } catch {
    // Idem: falhar aqui só faz a seta apontar para o hub por mais uma navegação.
  }
};

/**
 * Expira o marcador quando a navegação sai do destino que ele marcou.
 *
 * Vive num hook chamado no `_app`, e não no `useProductBackPath`, porque o hook da
 * seta só existe nas páginas que usam os layouts de produto. A saída pelo breadcrumb
 * "Comunidades" vai para `/products`, que não usa nenhum deles: o marcador sobrevivia
 * à saída e rearmava numa visita seguinte, feita pelo menu, que não partiu do hub.
 *
 * Aqui roda em toda mudança de rota, com layout ou sem.
 *
 * Descendentes do destino NÃO expiram, de propósito: `/offers/create` mantém a volta
 * viva para quando a pessoa subir de novo para `/offers`. Quem impede a seta da filha
 * de pular para o hub é a igualdade exata em `useProductBackPath`, não esta expiração.
 */
/**
 * Desvios que o próprio destino EXIGE antes de deixar cumprir o passo.
 *
 * O "indique e ganhe" é o caso: sem chave PIX cadastrada, `/referVib` não mostra o link de indicação —
 * mostra um aviso cujo único caminho é `/profile/personalData`. Como essa rota está fora do destino
 * marcado, a expiração apagava o marcador, e voltar para `/referVib` depois de cadastrar a chave já não
 * oferecia a volta ao checklist. O passo com pré-requisito era o único que não fechava o laço.
 *
 * Lista explícita, e não uma regra genérica de "qualquer rota de perfil": desvio que não está aqui
 * continua apagando o marcador, que é o comportamento certo para quem simplesmente saiu.
 */
const DESVIOS_OBRIGATORIOS: Record<string, string[]> = {
  '/referVib': ['/profile/personalData'],
};

const ehDesvioObrigatorio = (destino: string, caminho: string) =>
  (DESVIOS_OBRIGATORIOS[destino] ?? []).some(
    (desvio) => caminho === desvio || caminho.startsWith(`${desvio}/`),
  );

export const useExpiraVoltaParaHub = () => {
  const router = useRouter();

  useEffect(() => {
    const marcado = lerVoltaParaHub();
    if (!marcado) return;

    const caminho = router.asPath.split(/[?#]/)[0];
    const noDestinoOuAbaixo =
      caminho === marcado.destino ||
      caminho.startsWith(`${marcado.destino}/`) ||
      caminho === buildOnboardingHubHref(marcado.code) ||
      ehDesvioObrigatorio(marcado.destino, caminho);

    if (!noDestinoOuAbaixo) limparVoltaParaHub();
  }, [router.asPath]);
};
